import React, { useState, useEffect, useContext, useMemo } from "react";
import { Box, Typography } from "@material-ui/core";
import {
  ContactInfoWorkflow,
  IContactInfo,
  TravelerSelectStep,
  ContactInfoStep,
  Header,
  HotelNeedToKnowPanel,
  RoomDetails,
  MobilePopoverCard,
  BackButton,
  MobileFloatingButton,
  LoadingPopup,
  B2BSpinner,
  emailRegex,
  phoneRegex,
  Icon,
  IconName,
  InformationalModal as TreesModal,
  TravelWalletSingleSelectionStep,
  VoidWindowNotice,
  EarnOfferBanner,
  getHSPEarnOfferDescription,
  SanitizedHtml,
  NotificationBanner,
  BannerSeverity,
} from "halifax";
import {
  ADD_CONTACT_INFO,
  CheckInPolicy,
  LodgingCollectionEnum,
  CancellationReason,
  CancellationPolicyEnum,
} from "redmond";
import { RouteComponentProps } from "react-router-dom";

import "./styles.scss";
import clsx from "clsx";
import queryStringParser from "query-string";
import { CDN_ASSETS, getConfigTenant, isVesperTenant } from "@capone/common";
import { MobileHotelBookWorkflowConnectorProps } from "./container";
import {
  PriceBreakdown,
  HotelBookPassengerSelection,
  PaymentCard,
  TitleSummaryCard,
  HotelBookMobileButton,
  HotelBookSummaryPanel,
  PriceBreakdownDropdown,
  ReshopSuccessBanner,
  ConnectionRetrySuccessBanner,
  PriceChangeInlineMessage,
  AGENT_FEE,
} from "../../../components";
import {
  CONTACT_INFO_SAVE,
  CONTINUE,
  CONTACT_INFO_TITLE,
  CONTACT_INFO_SUBTITLE,
  NEED_TO_KNOW_TITLE,
  NEED_TO_KNOW_DETAILS,
  getCfarSecondaryText,
  REVIEW_MY_TRIP_TEXT,
  PRICE_QUOTE_MESSAGE,
  TREES_MODAL_CTA_TEXT,
  TREES_MODAL_HEADER,
  TREES_MODAL_SUBTITLE,
  TREES_MODAL_TITLE,
  TREES_BOLDED_MODAL_CTA_TEXT,
  CHECKOUT_PRICE_BREAKDOWN_HEADER,
  CHECK_IN_INSTRUCTIONS_TITLE,
  formattedCheckOut,
  formattedCheckIn,
} from "./textConstants";
import { PATH_HOME, PATH_BOOK_CONFIRMATION } from "../../../../../utils/paths";
import { ClientContext } from "../../../../../App";
import { HOTELS_REWARDS_DISABLED_BANNER_TEXT } from "../../../../rewards/components/RewardsBanner/constants";
import { trackEvent } from "../../../../../api/v0/analytics/trackEvent";
import { fetchCustomerDetails } from "../../../../../api/v0/customer/fetchCustomerDetails";
import {
  AVAILABLE,
  CREDIT_OFFER_STACKING_V1,
  getExperimentVariant,
  HOTELS_CALIFORNIA_BILL_644_EXPERIMENT,
  TRAVEL_WALLET_CREDITS_EXPERIMENT,
  TRAVEL_WALLET_OFFER_EXPERIMENT,
  TREES_MODAL_EXPERIMENT,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../../../../context/experiments";
import { TravelOfferSelection } from "../../TravelOfferSelection";
import PremierCollectionCheckoutBenefits from "../../PremierCollectionCheckoutBenefits";
import { CfarCancellationPolicy } from "../../../../ancillary/components/addOnComponents/cfar/CfarCancellationPolicy";

import { CALIFORNIA_BILL_644_CANCELLATION_TEXT } from "../DesktopHotelBookWorkflow/textConstants";
import {
  parseQueryString,
  getCancellationPolicyInfoFromQuote,
} from "../../../utils";
import dayjs from "dayjs";

export interface IMobileHotelBookWorkflowProps
  extends MobileHotelBookWorkflowConnectorProps,
    RouteComponentProps {}

export enum MobileHotelBookWorkflowStep {
  TravelerInformation,
  ContactInformation,
  TravelOfferSelection,
  RewardsAndPayment,
  Review,
}

export const MobileHotelBookWorkflow = ({
  priceQuote,
  hasNoUserPassengers,
  schedulePriceQuote,
  history,
  chosenProduct,
  selectedLodging,
  roomInfoProduct,
  chosenProductIndex,
  reservation,
  isBookingValid,
  setContactInfo,
  confirmationEmail,
  confirmationPhoneNumber,
  priceDifferenceAcknowledged,
  scheduleBook,
  priceQuoteInProgress,
  priceQuoteErrors,
  confirmationDetailsErrors,
  offers,
  resetPaymentCardSelectedAccounts,
  setPriceQuote,
  roomsCount,
  credit,
  isTravelWalletPaymentOnly,
  creditToApply,
  ancillaries,
  hasCfarAttached,
  checkIn,
  checkOut,
  cancellationSummary,
  isTravelApp,
  offerToApply,
  showRequoteSuccessBanner,
}: IMobileHotelBookWorkflowProps) => {
  const clientContext = useContext(ClientContext);
  const { sessionInfo, isAgentPortal, isRewardsDisabled } = clientContext;
  const isVesper = isVesperTenant(getConfigTenant());
  // TODO: remove this when we decide if using text message - https://hopchat.slack.com/archives/C01FX0M22MV/p1620146159000600
  const [contactInfo, setContact] = useState<IContactInfo | null>({
    phoneNumber: confirmationPhoneNumber || "",
    email: confirmationEmail || sessionInfo?.userInfo?.email || "",
  });
  const [customerDetailsLoading, setCustomerDetailsLoading] =
    React.useState<boolean>(true);
  const [checkoutStep, setCheckoutStep] =
    useState<MobileHotelBookWorkflowStep>(0);
  const [travelerWorkflowStep, setTravelerWorkflowStep] =
    useState<TravelerSelectStep>(TravelerSelectStep.Main);
  const [contactInfoStep, setContactInfoStep] = useState<ContactInfoStep>(
    ContactInfoStep.Main,
  );
  const [travelOfferSelectStep, setTravelOfferSelectStep] =
    useState<TravelWalletSingleSelectionStep>(
      TravelWalletSingleSelectionStep.Main,
    );
  const [openPaymentCard, setOpenPaymentCard] = useState<boolean>(false);
  const [treeModalOpen, setTreeModalOpen] = useState(false);
  const [updatedInfoOnReview, setUpdatedInfoOnReview] = useState(false);

  const expState = useExperiments();

  const treesModalExperiment = getExperimentVariant(
    expState.experiments,
    TREES_MODAL_EXPERIMENT,
  );
  const isTreesModalExperiment = useMemo(
    () => treesModalExperiment === AVAILABLE,
    [treesModalExperiment],
  );

  const travelWalletOffer = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_OFFER_EXPERIMENT,
  );
  const isTravelWalletOfferExperiment = React.useMemo(
    () => travelWalletOffer === AVAILABLE,
    [travelWalletOffer],
  );

  const travelWalletCreditsExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_CREDITS_EXPERIMENT,
  );
  const isTravelWalletCreditsExperiment = React.useMemo(
    () => travelWalletCreditsExperiment === AVAILABLE,
    [travelWalletCreditsExperiment],
  );

  const creditAndOfferStackingExperimentV1 = getExperimentVariant(
    expState.experiments,
    CREDIT_OFFER_STACKING_V1,
  );
  const isCreditAndOfferStackingExperimentV1 = useMemo(
    () => creditAndOfferStackingExperimentV1 === AVAILABLE,
    [creditAndOfferStackingExperimentV1],
  );

  const californiaBill644Variant = getExperimentVariant(
    expState.experiments,
    HOTELS_CALIFORNIA_BILL_644_EXPERIMENT,
  );
  const isCaliforniaBill644Experiment = useMemo(
    () => californiaBill644Variant === AVAILABLE,
    [californiaBill644Variant],
  );

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  // Whether the TravelOfferSelection step is skipped for this user. Drives both
  // forward navigation and the reshop-recovery landing step below.
  const shouldSkipTravelOfferSelectionStep = useMemo(
    () =>
      (isTravelWalletCreditsExperiment && !isCreditAndOfferStackingExperimentV1
        ? Math.abs(credit?.amount.amount ?? 0) > 0
        : true) &&
      (isTravelWalletOfferExperiment && !isCreditAndOfferStackingExperimentV1
        ? !offers?.length
        : true),
    [
      isTravelWalletCreditsExperiment,
      isCreditAndOfferStackingExperimentV1,
      isTravelWalletOfferExperiment,
      credit?.amount.amount,
      offers?.length,
    ],
  );

  const showFree24HourCancel =
    isCaliforniaBill644Experiment &&
    cancellationSummary?.reasons.includes(CancellationReason.CaliforniaBill644);

  const cancellationPolicyInfo = getCancellationPolicyInfoFromQuote({
    quote: priceQuote,
    product: chosenProduct,
    getCfarSecondaryText,
  });

  const onBookHotel = () => {
    const queryString = queryStringParser.parse(history.location.search);
    priceDifferenceAcknowledged || !!priceQuote
      ? scheduleBook({
          agentFee: isAgentPortal ? AGENT_FEE : 0,
          isRecommended: queryString.recommended === "true",
          ancillaries,
          initialTime: dayjs().toISOString(),
        })
      : schedulePriceQuote({
          history,
          agentFee: isAgentPortal ? AGENT_FEE : 0,
          ancillaries,
        });
  };

  const mergedPolicies = React.useMemo(
    () =>
      [
        ...(chosenProduct?.policies ?? []),
        ...(priceQuote?.checkInInstructions.policies ?? []),
      ].reduce((uniquePolicies, currentPolicy) => {
        if (
          !uniquePolicies.some(({ title }) => currentPolicy.title === title)
        ) {
          uniquePolicies.push(currentPolicy);
        }
        return uniquePolicies;
      }, [] as CheckInPolicy[]),
    [chosenProduct?.policies, priceQuote?.checkInInstructions.policies],
  );

  const openHotelBookPassengerSelection = () =>
    setTravelerWorkflowStep(TravelerSelectStep.TravelerSelect);

  const openContactInfoWorkflow = () => {
    setContactInfoStep(ContactInfoStep.ContactInfoForm);
  };

  const openTravelOffersSelection = () => {
    setTravelOfferSelectStep(
      TravelWalletSingleSelectionStep.TravelWalletSelection,
    );
  };

  const openPaymentSelection = () => setOpenPaymentCard(true);

  const incrementCheckoutStep = () => {
    setCheckoutStep((step) => {
      const searchParams = new URLSearchParams(history.location.search);
      if (
        (step === MobileHotelBookWorkflowStep.ContactInformation &&
          shouldSkipTravelOfferSelectionStep) ||
        (step === MobileHotelBookWorkflowStep.TravelOfferSelection &&
          isTravelWalletPaymentOnly)
      ) {
        if (isTravelApp) {
          searchParams.set("bookStep", (step + 2).toString());

          history.push({
            ...history.location,
            search: searchParams.toString(),
          });
        }

        return step + 2;
      }
      if (isTravelApp) {
        searchParams.set(
          "bookStep",
          (step === MobileHotelBookWorkflowStep.Review
            ? step
            : step + 1
          ).toString(),
        );

        history.push({
          ...history.location,
          search: searchParams.toString(),
        });
      }

      return step === MobileHotelBookWorkflowStep.Review ? step : step + 1;
    });
  };

  const parsedQueryString = useMemo(
    () => parseQueryString(history),
    [history.location.search],
  );

  useEffect(() => {
    if (isTravelApp) {
      if (!parsedQueryString?.bookStep) {
        const searchParams = new URLSearchParams(history.location.search);
        searchParams.set("bookStep", checkoutStep.toString());

        history.replace({
          ...history.location,
          search: searchParams.toString(),
        });
      } else if (
        parsedQueryString?.bookStep &&
        Number(parsedQueryString?.bookStep) !== checkoutStep
      ) {
        setCheckoutStep(Number(parsedQueryString?.bookStep));
      }
    }
  }, [checkoutStep, parsedQueryString?.bookStep, isTravelApp]);

  const handleGoBack = () => {
    switch (checkoutStep) {
      case MobileHotelBookWorkflowStep.TravelerInformation:
        if (selectedLodging) {
          history.goBack();
        } else {
          history.push(PATH_HOME);
        }
        break;
      case MobileHotelBookWorkflowStep.RewardsAndPayment:
        if (shouldSkipTravelOfferSelectionStep) {
          setCheckoutStep((step) => step - 2);
        } else {
          setCheckoutStep((step) => step - 1);
        }
        break;
      default:
        setCheckoutStep((step) => step - 1);
        break;
    }
  };

  const closeAllPopovers = () => {
    setTravelerWorkflowStep(TravelerSelectStep.Main);
    setContactInfoStep(ContactInfoStep.Main);
    setTravelOfferSelectStep(TravelWalletSingleSelectionStep.Main);
    setOpenPaymentCard(false);
  };

  const startSchedulePriceQuoteAndResetPayment = () => {
    schedulePriceQuote({
      history,
      agentFee: isAgentPortal ? AGENT_FEE : 0,
      pollQuoteOnly: true,
      ancillaries,
    });
    resetPaymentCardSelectedAccounts();
    setUpdatedInfoOnReview(true);
  };

  useEffect(() => {
    if (priceQuoteErrors.length > 0 || confirmationDetailsErrors.length > 0) {
      setCheckoutStep(MobileHotelBookWorkflowStep.TravelerInformation);
    }
  }, [priceQuoteErrors.length, confirmationDetailsErrors.length]);

  // After a reshop or connection-retry (CT1261T-419) re-quote succeeds, advance
  // off TravelerInformation (the error demoted it) to the payment step, mirroring
  // desktop. Keyed on priceQuoteErrors clearing (not the banner) so a second
  // re-quote re-fires; the banner owns its own dismissal.
  useEffect(() => {
    if (!showRequoteSuccessBanner || priceQuoteErrors.length > 0) return;

    const reshopReturnStep = shouldSkipTravelOfferSelectionStep
      ? MobileHotelBookWorkflowStep.RewardsAndPayment
      : MobileHotelBookWorkflowStep.TravelOfferSelection;

    if (isTravelApp) {
      const searchParams = new URLSearchParams(history.location.search);
      searchParams.set("bookStep", reshopReturnStep.toString());
      history.push({
        ...history.location,
        search: searchParams.toString(),
      });
    }

    setCheckoutStep(reshopReturnStep);
  }, [
    priceQuoteErrors.length,
    showRequoteSuccessBanner,
    shouldSkipTravelOfferSelectionStep,
  ]);

  useEffect(() => {
    switch (checkoutStep) {
      case MobileHotelBookWorkflowStep.TravelerInformation:
        closeAllPopovers();
        openHotelBookPassengerSelection();
        break;
      case MobileHotelBookWorkflowStep.ContactInformation:
        closeAllPopovers();
        openContactInfoWorkflow();
        break;
      case MobileHotelBookWorkflowStep.TravelOfferSelection:
        closeAllPopovers();
        openTravelOffersSelection();
        break;
      case MobileHotelBookWorkflowStep.RewardsAndPayment:
        closeAllPopovers();
        openPaymentSelection();
        break;
      default:
        break;
    }
  }, [checkoutStep]);

  // note: it handles the PassengerSelection step logic separately from the previous useEffect;
  // when travelerWorkflowStep is being changed too rapidly (e.g.: changing from Main -> TravelerSelect -> TravelerInfoForm because of hasUserPassengers value)
  // it seems to cause some unexpected behaviours on TravelerSelectWorkflow, which in turn causes the Review screen to be unscrollable
  // https://hopper-jira.atlassian.net/browse/BP-1090
  useEffect(() => {
    if (
      checkoutStep === MobileHotelBookWorkflowStep.TravelerInformation &&
      hasNoUserPassengers
    ) {
      setTravelerWorkflowStep(TravelerSelectStep.TravelerInfoForm);
    }
  }, [checkoutStep, hasNoUserPassengers]);

  useEffect(() => {
    if (reservation) {
      const params = isTravelApp ? "?isTravelApp=true" : "";
      history.push(`${PATH_BOOK_CONFIRMATION}${params}`);
    }
  }, [reservation]);

  useEffect(() => {
    const getCustomerDetails = async () => {
      try {
        const details = await fetchCustomerDetails();
        setContact({
          email: "",
          ...contactInfo,
          phoneNumber: details?.phoneNumber || "",
        });
        setContactInfo(contactInfo?.email || "", details?.phoneNumber || "");
      } finally {
        setCustomerDetailsLoading(false);
      }
    };

    getCustomerDetails();
  }, []);

  useEffect(() => {
    if (
      checkoutStep === MobileHotelBookWorkflowStep.Review &&
      !priceQuoteInProgress &&
      !!priceQuote &&
      updatedInfoOnReview
    ) {
      if (creditToApply) {
        setCheckoutStep(MobileHotelBookWorkflowStep.TravelOfferSelection); // if user has selected a travel wallet item before PQ is run again on pax/contact info update, bring them back to that step
      } else {
        handleGoBack();
      }
      setUpdatedInfoOnReview(false);
    }
  }, [checkoutStep, priceQuoteInProgress, priceQuote, updatedInfoOnReview]);

  useEffect(() => {
    if (priceQuoteInProgress && !!priceQuote) {
      setPriceQuote({
        priceQuote: null,
        pricingWithAncillaries: null,
        hotelAncillaryQuotes: null,
        type: "hotel-checkout",
      });
    }
  }, [priceQuoteInProgress]);

  return (
    <>
      <Box
        className={clsx(
          "mobile-hotel-book-workflow-root",
          "display-room-details-modal",
          {
            redesigned: isVesper,
            "mobile-review-hotel-book":
              checkoutStep === MobileHotelBookWorkflowStep.Review,
            "travel-app": isTravelApp,
          },
        )}
      >
        {/* TODO: Header should have pricing details */}
        <Header
          className={clsx("mobile-hotel-book-header", "checkout")}
          left={
            !isTravelApp ? (
              <BackButton
                className="mobile-hotel-book-header-go-back"
                onClick={handleGoBack}
              />
            ) : undefined
          }
          right={
            <PriceBreakdownDropdown
              popoverClassName={
                checkoutStep === MobileHotelBookWorkflowStep.Review
                  ? "mobile-review-hotel-book-price-breakdown"
                  : undefined
              }
            />
          }
          isMobile
          fullWidth
        />
        {/*
          Rendered after the mobile header so its sticky placeholder sits below
          the header (the mobile analog of the desktop stepper placement).
        */}
        <ReshopSuccessBanner />
        <ConnectionRetrySuccessBanner />
        <PriceChangeInlineMessage />
        <TitleSummaryCard isMobile isXmodRedesign={isXmodRedesign} />
        {showFree24HourCancel && <VoidWindowNotice />}
        <HotelBookSummaryPanel isMobile className="b2b" />
        {!!roomInfoProduct && chosenProductIndex !== null && (
          <RoomDetails
            roomInfoProduct={roomInfoProduct}
            productIndex={chosenProductIndex}
            lodging={selectedLodging}
            isMobile
            hideAmenities
            roomsCount={roomsCount}
            hideRoomBedDescription
            hideRoomCapacity
            showAmenitiesModal
          />
        )}
        <HotelBookPassengerSelection
          progress={travelerWorkflowStep}
          setProgress={setTravelerWorkflowStep}
          onGoBack={(travelersChanged) => {
            switch (checkoutStep) {
              case MobileHotelBookWorkflowStep.TravelerInformation:
                handleGoBack();
                break;
              case MobileHotelBookWorkflowStep.Review:
                // note: when the user changes passengers on the final step and then clicks the go-back button,
                // handle it as if the user clicked continue;
                if (travelersChanged) {
                  startSchedulePriceQuoteAndResetPayment();
                }
                break;
              default:
                break;
            }
          }}
          onContinue={(travelersChanged) => {
            switch (checkoutStep) {
              case MobileHotelBookWorkflowStep.Review:
                if (travelersChanged) {
                  startSchedulePriceQuoteAndResetPayment();
                }
                break;
              default:
                incrementCheckoutStep();
                break;
            }
          }}
          selectionScreenHeaderElement={
            <PriceBreakdownDropdown
              dataTestIds={{
                popover: "hotel-passenger-selection-price-breakdown-dialog",
                trigger:
                  "hotel-passenger-selection-price-breakdown-dialog-trigger",
              }}
            />
          }
          className="b2b"
          onTravelersStep={
            checkoutStep === MobileHotelBookWorkflowStep.TravelerInformation
          }
          isMobile
          useLocalId
          hiddenTravelerIds={[]}
          showVoidWindowNotice={showFree24HourCancel}
          useXmodHeadings={isXmodRedesign}
        />
        <ContactInfoWorkflow
          titles={{
            contactInfoTitle: CONTACT_INFO_TITLE,
            contactInfoSubtitle: CONTACT_INFO_SUBTITLE,
            buttonTitle:
              checkoutStep === MobileHotelBookWorkflowStep.Review
                ? CONTACT_INFO_SAVE
                : CONTINUE,
          }}
          progressStep={contactInfoStep}
          setProgressStep={setContactInfoStep}
          isMobile
          contactInfo={contactInfo}
          onGoBack={() => {
            if (
              checkoutStep === MobileHotelBookWorkflowStep.ContactInformation
            ) {
              handleGoBack();
            }
          }}
          onContactInfoChange={(contactInfo) => {
            setContact(contactInfo);
            setContactInfo(contactInfo.email, contactInfo.phoneNumber);
            if (
              contactInfo.email &&
              contactInfo.phoneNumber &&
              emailRegex.test(contactInfo.email) &&
              phoneRegex.test(contactInfo.phoneNumber)
            ) {
              trackEvent({
                eventName: ADD_CONTACT_INFO,
                properties: {},
              });
            }
            incrementCheckoutStep();
          }}
          mobileHeaderElement={<PriceBreakdownDropdown />}
          className="b2b"
          onContinueClick={() => {
            switch (checkoutStep) {
              case MobileHotelBookWorkflowStep.Review:
                startSchedulePriceQuoteAndResetPayment();
                break;
              default:
                schedulePriceQuote({
                  history,
                  agentFee: isAgentPortal ? AGENT_FEE : 0,
                  pollQuoteOnly: true,
                  ancillaries,
                });
                break;
            }
          }}
          loading={customerDetailsLoading}
          bottomContent={
            showFree24HourCancel ? <VoidWindowNotice /> : undefined
          }
          hideBackButton={isTravelApp}
          useXmodHeadings={isXmodRedesign}
        />
        {selectedLodging?.lodgingCollection ===
          LodgingCollectionEnum.Premier && (
          <PremierCollectionCheckoutBenefits />
        )}
        {isTravelWalletOfferExperiment &&
          !isCreditAndOfferStackingExperimentV1 && (
            <TravelOfferSelection
              progressStep={travelOfferSelectStep}
              mobileHeaderElement={<PriceBreakdownDropdown />}
              isMobile
              onGoBack={() => {
                if (
                  checkoutStep ===
                  MobileHotelBookWorkflowStep.TravelOfferSelection
                ) {
                  handleGoBack();
                }
              }}
              onContinueClick={() => {
                incrementCheckoutStep();
                isTravelWalletPaymentOnly &&
                  setTravelOfferSelectStep(
                    TravelWalletSingleSelectionStep.Main,
                  );
              }}
            />
          )}
        <Box className="mobile-hotels-price-breakdown">
          <Typography
            className="price-breakdown-header"
            variant="h4"
            component="h2"
          >
            {CHECKOUT_PRICE_BREAKDOWN_HEADER}
          </Typography>
          <PriceBreakdown />
        </Box>
        {getHSPEarnOfferDescription(offerToApply) && (
          <EarnOfferBanner isMobile />
        )}

        {hasCfarAttached ? (
          <CfarCancellationPolicy />
        ) : cancellationPolicyInfo ? (
          <Box className="cancellation-details">
            <Typography variant="h4" component="h2">
              {cancellationPolicyInfo.primaryText}
            </Typography>
            <Typography variant="body2">
              {showFree24HourCancel &&
              chosenProduct?.cancellationPolicy.CancellationPolicy ===
                CancellationPolicyEnum.NonRefundable
                ? CALIFORNIA_BILL_644_CANCELLATION_TEXT
                : cancellationPolicyInfo.secondaryText}
            </Typography>
          </Box>
        ) : null}
        {selectedLodging?.lodging?.checkInInstructions &&
          checkIn &&
          checkOut && (
            <Box className={clsx("check-in-instructions")}>
              <Typography
                variant="h4"
                component="h2"
                className="hotel-shop-check-in-instructions-heading"
              >
                {CHECK_IN_INSTRUCTIONS_TITLE}
              </Typography>
              <SanitizedHtml
                as={Typography}
                variant="body2"
                html={formattedCheckIn(
                  checkIn,
                  selectedLodging.lodging.checkInInstructions?.checkInTime,
                )}
              />
              <SanitizedHtml
                as={Typography}
                variant="body2"
                html={formattedCheckOut(
                  checkOut,
                  selectedLodging.lodging.checkInInstructions?.checkOutTime,
                )}
              />
            </Box>
          )}
        <HotelNeedToKnowPanel
          title={NEED_TO_KNOW_TITLE}
          subtitle={NEED_TO_KNOW_DETAILS}
          policies={mergedPolicies}
          fetchingPriceQuote={priceQuoteInProgress}
          className={clsx({ "less-padding": isTreesModalExperiment })}
        />
        {isRewardsDisabled && (
          <NotificationBanner
            className="rewards-disabled-banner"
            label={HOTELS_REWARDS_DISABLED_BANNER_TEXT}
            severity={BannerSeverity.WARNING}
            icon={<Icon className="banner-icon" name={IconName.WarningAlert} />}
          />
        )}
        {isTreesModalExperiment && (
          <TreesModal
            image={CDN_ASSETS.capone.trees.confirmation}
            header={TREES_MODAL_HEADER}
            title={TREES_MODAL_TITLE}
            onClose={() => setTreeModalOpen(false)}
            subtitle={TREES_MODAL_SUBTITLE}
            icon={<Icon className="trees-icon" name={IconName.TreesIcon} />}
            openModal={treeModalOpen}
            setOpenModal={setTreeModalOpen}
            modalLinkCopy={TREES_MODAL_CTA_TEXT}
            modalButtonCopyStartIcon={
              <Icon className="trees-icon" name={IconName.TreesIcon} />
            }
            isMobile
            boldedModalLinkCopy={TREES_BOLDED_MODAL_CTA_TEXT}
          />
        )}

        <HotelBookMobileButton
          hasContactInfo={!!contactInfo}
          openContactInfo={() => {
            setContactInfoStep(ContactInfoStep.ContactInfoForm);
          }}
          isBookingValid={isBookingValid}
          onConfirmAndBook={() => onBookHotel()}
          // TODO: simplify/trim HotelBookMobileButton logic
          showPaymentStep
          onApplyRewards={() => {}}
          paymentCardOpen={openPaymentCard}
          isXmodRedesign={isXmodRedesign}
        />
      </Box>
      <MobilePaymentCardPopover
        openPaymentCard={openPaymentCard}
        disabled={!isBookingValid}
        onClose={() => setOpenPaymentCard(false)}
        onConfirm={() => {
          incrementCheckoutStep();
          setOpenPaymentCard(false);
        }}
        onGoBack={() => {
          if (checkoutStep === MobileHotelBookWorkflowStep.RewardsAndPayment) {
            handleGoBack();
          }
        }}
        mobileHeaderElement={<PriceBreakdownDropdown />}
        isTravelApp={isTravelApp}
      />
      <LoadingPopup
        classes={["mobile-price-quote-loading-popup"]}
        open={priceQuoteInProgress}
        message={PRICE_QUOTE_MESSAGE}
        indicator={B2BSpinner}
        indicatorSize="small"
        verticalAlignment="center"
        fullScreen
      />
    </>
  );
};

interface IMobilePaymentCardPopoverProps {
  openPaymentCard: boolean;
  disabled?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onGoBack: () => void;
  mobileHeaderElement?: JSX.Element;
  isTravelApp?: boolean;
}

const MobilePaymentCardPopover = (props: IMobilePaymentCardPopoverProps) => {
  const {
    openPaymentCard,
    disabled = true,
    onClose,
    onConfirm,
    onGoBack,
    mobileHeaderElement,
    isTravelApp,
  } = props;

  return (
    <MobilePopoverCard
      open={openPaymentCard}
      className={clsx("hotel-payment-card-popup", "b2b")}
      fullScreen
      onClose={onClose}
      topLeftButton={
        !isTravelApp ? (
          <BackButton
            className="hotel-payment-card-popup-back-button"
            onClick={onGoBack}
          />
        ) : undefined
      }
      headerElement={mobileHeaderElement}
    >
      <PaymentCard />
      {!disabled && (
        <MobileFloatingButton
          className="hotel-book-review-trip-button"
          onClick={onConfirm}
          wrapperClassName="b2b"
        >
          {REVIEW_MY_TRIP_TEXT}
        </MobileFloatingButton>
      )}
    </MobilePopoverCard>
  );
};
